import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, streamChat } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { CoachAvatar, COACH_IMG } from "@/components/CoachAvatar";
import { PlayVoiceButton } from "@/components/PlayVoiceButton";
import { FavoriteButton } from "@/components/FavoriteButton";
import { UpgradePanel } from "@/components/UpgradePanel";
import { ArrowLeft, Send } from "lucide-react";
import { toast } from "sonner";

export default function Chat() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamBuf, setStreamBuf] = useState("");
  const [voicePlaying, setVoicePlaying] = useState(false);
  const [shouldGreet, setShouldGreet] = useState(false);
  const [greeting, setGreeting] = useState("");
  const [savedIds, setSavedIds] = useState(new Set());
  const [billing, setBilling] = useState(null);
  const scrollRef = useRef(null);
  const taRef = useRef(null);

  const loadBilling = () => api.get("/billing/status").then((r) => setBilling(r.data)).catch(() => {});
  useEffect(() => {
    loadBilling();
  }, []);
  const limitReached = !!billing && billing.remaining_today === 0 && !session?.ai_paused;

  useEffect(() => {
    api.get("/greeting").then((r) => setGreeting(r.data.text)).catch(() => {});
    api.get("/favorites").then((r) => setSavedIds(new Set(r.data.map((f) => f.message_id)))).catch(() => {});
  }, []);

  const toggleFavorite = async (messageId, saved) => {
    try {
      if (saved) {
        await api.delete(`/favorites/${messageId}`);
        setSavedIds((s) => { const n = new Set(s); n.delete(messageId); return n; });
      } else {
        await api.post("/favorites", { message_id: messageId });
        setSavedIds((s) => new Set(s).add(messageId));
        toast.success("Saved to your Words to live by");
      }
    } catch {
      toast.error("Couldn't update your saved words");
    }
  };

  useEffect(() => {
    const key = `india-greeted-${sessionId}`;
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, "1");
      setShouldGreet(true);
    }
  }, [sessionId]);

  useEffect(() => {
    const onSpeak = (e) => setVoicePlaying(e.detail);
    window.addEventListener("india-voice-speaking", onSpeak);
    return () => window.removeEventListener("india-voice-speaking", onSpeak);
  }, []);

  const load = async () => {
    try {
      const { data } = await api.get(`/sessions/${sessionId}`);
      setSession(data.session);
      setMessages(data.messages || []);
    } catch (e) {
      toast.error("Session not found");
      navigate("/dashboard");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    if (streaming) return;
    const t = setInterval(() => {
      api.get(`/sessions/${sessionId}`).then(({ data }) => {
        setSession(data.session);
        setMessages(data.messages || []);
      }).catch(() => {});
    }, 5000);
    return () => clearInterval(t);
  }, [sessionId, streaming]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, streamBuf]);

  const send = async () => {
    const content = input.trim();
    if (!content || streaming || limitReached) return;
    setInput("");
    if (taRef.current) taRef.current.style.height = "auto";

    // Optimistically add user message
    const tempUser = {
      id: `tmp-${Date.now()}`,
      session_id: sessionId,
      role: "user",
      content,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, tempUser]);
    setStreaming(true);
    setStreamBuf("");

    let acc = "";
    await streamChat(
      sessionId,
      content,
      (delta) => {
        acc += delta;
        setStreamBuf(acc);
      },
      async () => {
        setStreaming(false);
        setStreamBuf("");
        // Reload for canonical ordering
        try {
          const { data } = await api.get(`/sessions/${sessionId}`);
          setSession(data.session);
          setMessages(data.messages || []);
        } catch {}
      },
      (err) => {
        setStreaming(false);
        setStreamBuf("");
        let detail = null;
        try { detail = JSON.parse(err).detail; } catch {}
        if (detail?.code === "daily_limit") {
          setMessages((m) => m.filter((x) => x.id !== tempUser.id));
          setInput(content);
          loadBilling();
          return;
        }
        toast.error((typeof detail === "string" && detail) || "Something went wrong");
      }
    );
    loadBilling();
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const autoGrow = (e) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 200) + "px";
  };

  return (
    <div className="h-[100dvh] lg:h-auto lg:min-h-screen bg-[#FDFBF7] flex flex-col">
      <AppHeader />

      <div className="flex-1 min-h-0 max-w-7xl w-full mx-auto px-3 md:px-10 py-3 md:py-6 flex flex-col lg:grid lg:grid-cols-5 gap-3 md:gap-6">
        {/* Coach bar (phones) */}
        <div className="lg:hidden flex items-center gap-3 px-1" data-testid="chat-mobile-coach-bar">
          <Link to="/dashboard" aria-label="Back to dashboard" data-testid="back-to-dashboard-mobile"
            className="h-10 w-10 -ml-1 shrink-0 flex items-center justify-center rounded-full text-[#1C221D] hover:bg-[#F2EBE5]">
            <ArrowLeft size={20} />
          </Link>
          <div className={`relative w-10 h-10 shrink-0 rounded-full overflow-hidden ${streaming || voicePlaying ? "coach-speaking" : ""}`}>
            <img src={COACH_IMG} alt="India Sadie" className="w-full h-full object-cover object-[50%_30%]" />
          </div>
          <div className="min-w-0">
            <div className="font-serif-display text-lg leading-tight text-[#1C221D]">India Sadie</div>
            <div className="text-xs text-[#5C6A61] truncate" data-testid="session-title-mobile">
              {streaming ? "thinking with you…" : session?.title || "New conversation"}
            </div>
          </div>
        </div>

        {/* Coach pane */}
        <aside className="hidden lg:col-span-2 lg:flex flex-col lg:sticky lg:top-24 lg:self-start lg:h-[calc(100vh-8rem)]">
          <Link
            to="/dashboard"
            className="eyebrow text-[#5C6A61] inline-flex items-center gap-2 hover:text-[#1C221D] mb-4"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft size={14} /> dashboard
          </Link>
          <div className="bg-white rounded-3xl p-8 border border-[#2A3B32]/10 shadow-[0_8px_30px_rgba(42,59,50,0.04)] flex-1 flex flex-col items-center text-center">
            <CoachAvatar size="xl" speaking={streaming || voicePlaying} />
            <div className="mt-6 font-serif-display text-3xl text-[#1C221D]">India Sadie</div>
            <div className="eyebrow text-[#5C6A61] mt-1">
              certified coach · {session?.category || "general"}
            </div>
            <div className="font-serif-display italic text-sm text-[#5C6A61] mt-1">
              Live the life you've always wanted.
            </div>
            {streaming ? (
              <div className="mt-5 flex items-center gap-1.5 text-[#5C6A61]" data-testid="coach-typing">
                <span className="typing-dot w-2 h-2 rounded-full bg-[#81B29A] inline-block" />
                <span className="typing-dot w-2 h-2 rounded-full bg-[#81B29A] inline-block" />
                <span className="typing-dot w-2 h-2 rounded-full bg-[#81B29A] inline-block" />
                <span className="ml-2 text-sm">thinking with you…</span>
              </div>
            ) : (
              <p className="mt-5 text-[#5C6A61] leading-relaxed max-w-xs">
                I'm here. Say what's on your mind — nothing is too small.
              </p>
            )}
          </div>
        </aside>

        {/* Chat pane */}
        <section className="flex-1 min-h-0 lg:col-span-3 flex flex-col bg-white rounded-3xl border border-[#2A3B32]/10 shadow-[0_8px_30px_rgba(42,59,50,0.04)] overflow-hidden lg:min-h-[70vh]">
          <div className="hidden lg:block px-6 py-5 border-b border-[#2A3B32]/8">
            <div className="eyebrow text-[#5C6A61]">conversation</div>
            <div className="font-serif-display text-xl text-[#1C221D] truncate" data-testid="session-title">
              {session?.title || "New conversation"}
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto chat-scroll px-4 md:px-6 py-5 md:py-6 space-y-4" data-testid="chat-feed">
            {messages.length === 0 && !streaming && session && greeting && (
              <div className="flex flex-col items-start fade-up" data-testid="greeting-bubble">
                <div className="bg-[#F2EBE5] rounded-2xl rounded-bl-sm p-5 max-w-lg text-[#1C221D] leading-relaxed">
                  {greeting}
                </div>
                <PlayVoiceButton messageId="greeting" audioPath="/greeting/audio" autoPlay={shouldGreet} />
              </div>
            )}
            {messages.map((m) => (
              <div
                key={m.id}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} fade-up`}
                data-testid={`msg-${m.role}`}
              >
                <div className={`flex flex-col max-w-[85%] ${m.role === "user" ? "items-end" : "items-start"}`}>
                  {m.role === "india" && (
                    <span className="eyebrow text-[#E07A5F] mb-1" data-testid="personal-reply-label">personal reply from India</span>
                  )}
                  <div
                    className={
                      m.role === "user"
                        ? "bg-[#2A3B32] text-[#FDFBF7] rounded-2xl rounded-br-sm px-5 py-3 leading-relaxed whitespace-pre-wrap"
                        : m.role === "india"
                        ? "bg-[#F2CC8F]/50 text-[#1C221D] rounded-2xl rounded-bl-sm px-5 py-3 leading-relaxed whitespace-pre-wrap"
                        : "bg-[#F2EBE5] text-[#1C221D] rounded-2xl rounded-bl-sm px-5 py-3 leading-relaxed whitespace-pre-wrap"
                    }
                  >
                    {m.content}
                  </div>
                  {m.role !== "user" && !String(m.id).startsWith("tmp-") && (
                    <div className="flex items-center gap-2">
                      <PlayVoiceButton messageId={m.id} />
                      <FavoriteButton messageId={m.id} saved={savedIds.has(m.id)} onToggle={toggleFavorite} />
                    </div>
                  )}
                </div>
              </div>
            ))}
            {session?.ai_paused && messages.length > 0 && messages[messages.length - 1].role === "user" && (
              <div className="text-sm text-[#5C6A61] italic" data-testid="awaiting-india-note">
                India will reply to you personally. You'll see her answer here.
              </div>
            )}
            {streaming && streamBuf && (
              <div className="flex justify-start fade-up" data-testid="msg-streaming">
                <div className="bg-[#F2EBE5] text-[#1C221D] rounded-2xl rounded-bl-sm px-5 py-3 max-w-[85%] leading-relaxed whitespace-pre-wrap">
                  {streamBuf}
                  <span className="inline-block w-1 h-4 bg-[#2A3B32] ml-1 align-middle animate-pulse" />
                </div>
              </div>
            )}
            {limitReached && !billing.owns_program && (
              <div className="pt-4">
                <UpgradePanel reason={`You've used your ${billing.daily_limit} free coaching messages for today, baby.`} />
              </div>
            )}
          </div>

          <div className="border-t border-[#2A3B32]/8 p-3 md:p-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] bg-[#FDFBF7]">
            {limitReached ? (
              <div className="text-sm text-[#1C221D] px-1 py-2" data-testid="limit-reached-note">
                You've used all {billing.daily_limit} coaching messages for today. They refresh{" "}
                {new Date(billing.resets_at).toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" })}.
                {!billing.owns_program && " Or unlock 20 a day with the Discipline Reset above."}
              </div>
            ) : (
            <>
            <div className="flex items-end gap-2 md:gap-3">
              <textarea
                ref={taRef}
                rows={1}
                value={input}
                onChange={autoGrow}
                onKeyDown={onKeyDown}
                onFocus={() => setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 300)}
                enterKeyHint="send"
                autoCapitalize="sentences"
                placeholder="Say what's on your mind…"
                data-testid="chat-input"
                className="flex-1 min-h-[48px] resize-none px-4 py-3 rounded-3xl border border-[#2A3B32]/15 bg-white focus:outline-none focus:border-[#2A3B32] focus:ring-2 focus:ring-[#81B29A]/30 transition-colors text-base text-[#1C221D] leading-relaxed"
              />
              <button
                onClick={send}
                disabled={!input.trim() || streaming}
                data-testid="chat-send"
                className="h-12 w-12 shrink-0 rounded-full bg-[#2A3B32] text-[#FDFBF7] flex items-center justify-center hover:-translate-y-0.5 active:scale-95 transition-transform duration-200 disabled:opacity-50"
              >
                <Send size={18} />
              </button>
            </div>
            <div className="mt-2 text-xs text-[#5C6A61] px-1 flex justify-between gap-3">
              <span className="hidden md:inline">Press Enter to send · Shift+Enter for a new line</span>
              {billing && !session?.ai_paused && (
                <span data-testid="chat-remaining">{billing.remaining_today} of {billing.daily_limit} coaching messages left today</span>
              )}
            </div>
            </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
